/**
 * Backoff for reopening a dropped SSE stream.
 *
 * WHY THIS EXISTS. `EventSource` retries by itself, but only for a transport
 * blip. When the server answers with a non-2xx — a 401 once a session token
 * expires being the everyday case — the browser fires `error`, sets
 * `readyState` to CLOSED, and never tries again. The stream is then dead with
 * no visible sign, and the page keeps rendering whatever it last received. That
 * is exactly the "goes stale until I refresh" symptom: a refresh works because
 * it opens a new EventSource, not because it fixes anything.
 *
 * So the reconnect is ours to drive. Both surfaces (console feed, client relay)
 * share this so their retry behaviour cannot drift apart.
 */
export const BACKOFF_BASE_MS = 1_000;
export const BACKOFF_MAX_MS = 30_000;
export const BACKOFF_JITTER_RATIO = 0.25;
/**
 * Delay before retry number `attempt` (0-based: attempt 0 is the first retry
 * after a drop). Exponential to the cap, then flat, with jitter subtracted so
 * the result is never longer than the cap.
 */
export function nextDelayMs(attempt, opts = {}) {
    const base = opts.baseMs ?? BACKOFF_BASE_MS;
    const max = opts.maxMs ?? BACKOFF_MAX_MS;
    const jitterRatio = opts.jitterRatio ?? BACKOFF_JITTER_RATIO;
    const random = opts.random ?? Math.random;
    const safeAttempt = Number.isFinite(attempt) && attempt > 0 ? Math.floor(attempt) : 0;
    // Cap the exponent before computing the power: 2 ** 1e9 is Infinity, and
    // Math.min(Infinity, max) is fine, but Infinity * jitter is NaN.
    const exponent = Math.min(safeAttempt, 32);
    const uncapped = base * 2 ** exponent;
    const capped = Math.min(uncapped, max);
    const jitter = capped * jitterRatio * random();
    return Math.max(0, Math.round(capped - jitter));
}
